import PageToolbar from '@/components/linear/page-toolbar';
import { invoicesPath } from '@/lib/linear-paths';
import { formatUsdFromCents, type Customer, type InvoiceDraft } from '@/types/invoice';
import { useForm } from '@inertiajs/react';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';

interface LineDraft {
  key: string;
  description: string;
  quantity: string;
  unitAmount: string;
}

interface InvoiceFormState {
  customerId: string;
  customerName: string;
  customerEmail: string;
  memo: string;
  dueOn: string;
  lines: LineDraft[];
}

interface Props {
  customers?: Customer[];
  invoice?: InvoiceDraft | null;
}

export default function New({ customers = [], invoice = null }: Props) {
  const [initial] = useState(() => buildInitialForm(invoice));
  const nextLineId = useRef(initial.lines.length + 1);
  const form = useForm<InvoiceFormState>(initial);
  const selectedCustomer =
    customers.find((customer) => customer.id === form.data.customerId) ?? null;
  const totalCents = form.data.lines.reduce((sum, line) => {
    return sum + wholeQuantity(line.quantity) * dollarsToCents(line.unitAmount);
  }, 0);

  const addLine = () => {
    const key = `line-${nextLineId.current}`;
    nextLineId.current += 1;
    form.setData((data) => ({
      ...data,
      lines: [...data.lines, blankLine(key)],
    }));
  };

  const updateLine = (key: string, patch: Partial<Omit<LineDraft, 'key'>>) => {
    form.setData((data) => ({
      ...data,
      lines: data.lines.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    }));
  };

  const removeLine = (key: string) => {
    form.setData((data) => {
      if (data.lines.length === 1) return data;

      return {
        ...data,
        lines: data.lines.filter((line) => line.key !== key),
      };
    });
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    form.transform((data) => ({
      invoice: {
        ...(data.customerId
          ? { customerId: data.customerId }
          : { customerName: data.customerName, customerEmail: data.customerEmail }),
        memo: data.memo,
        dueOn: data.dueOn,
        currency: 'usd',
        lines: data.lines.map((line) => ({
          description: line.description,
          quantity: wholeQuantity(line.quantity),
          unitAmountCents: dollarsToCents(line.unitAmount),
        })),
      },
    }));
    form.post(invoicesPath());
  };

  return (
    <div className="ln-page">
      <PageToolbar title="New invoice" showNew={false} />
      <div className="ln-page-scroll">
        <div className="ln-form-page" style={{ maxWidth: 720 }}>
          <h1>New invoice</h1>
          <p className="ln-lede">Bill an existing customer, or add one as you write the invoice.</p>
          <form onSubmit={submit}>
            <Field label="Customer" htmlFor="invoice_customer_id" error={form.errors.customerId}>
              <select
                id="invoice_customer_id"
                className="ln-filter-input"
                value={form.data.customerId}
                onChange={(event) => {
                  const customerId = event.target.value;
                  form.setData((data) => ({
                    ...data,
                    customerId,
                    customerName: customerId ? '' : data.customerName,
                    customerEmail: customerId ? '' : data.customerEmail,
                  }));
                }}
                style={{ width: '100%' }}
              >
                <option value="">New customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
              {selectedCustomer?.email ? (
                <p className="ln-inbox-sub">{selectedCustomer.email}</p>
              ) : null}
            </Field>

            {form.data.customerId ? null : (
              <>
                <Field
                  label="Customer name"
                  htmlFor="invoice_customer_name"
                  error={form.errors.customerName}
                >
                  <input
                    id="invoice_customer_name"
                    className="ln-filter-input"
                    value={form.data.customerName}
                    onChange={(event) => form.setData('customerName', event.target.value)}
                    autoComplete="name"
                    required
                    style={{ width: '100%' }}
                  />
                </Field>
                <Field
                  label="Customer email"
                  htmlFor="invoice_customer_email"
                  error={form.errors.customerEmail}
                >
                  <input
                    id="invoice_customer_email"
                    className="ln-filter-input"
                    type="email"
                    value={form.data.customerEmail}
                    onChange={(event) => form.setData('customerEmail', event.target.value)}
                    autoComplete="email"
                    required
                    style={{ width: '100%' }}
                  />
                </Field>
              </>
            )}

            <Field label="Memo" htmlFor="invoice_memo" error={form.errors.memo}>
              <textarea
                id="invoice_memo"
                className="ln-filter-input"
                value={form.data.memo}
                onChange={(event) => form.setData('memo', event.target.value)}
                rows={3}
                style={{ width: '100%', height: 'auto', minHeight: 72, padding: 8 }}
              />
            </Field>

            <Field label="Due date" htmlFor="invoice_due_on" error={form.errors.dueOn}>
              <input
                id="invoice_due_on"
                className="ln-filter-input"
                type="date"
                value={form.data.dueOn}
                onChange={(event) => form.setData('dueOn', event.target.value)}
              />
            </Field>

            <Field label="Currency" htmlFor="invoice_currency">
              <input
                id="invoice_currency"
                className="ln-filter-input"
                value="USD"
                readOnly
                aria-readonly="true"
              />
            </Field>

            <div style={{ marginBottom: 8, fontSize: '0.75rem', color: 'var(--ln-muted)' }}>
              Line items
            </div>
            {form.data.lines.map((line, index) => (
              <div
                key={line.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) 88px 128px auto',
                  gap: 8,
                  marginBottom: 8,
                  alignItems: 'end',
                }}
              >
                <Field
                  compact
                  label={index === 0 ? 'Description' : ''}
                  htmlFor={`invoice_line_${line.key}`}
                >
                  <input
                    id={`invoice_line_${line.key}`}
                    className="ln-filter-input"
                    value={line.description}
                    onChange={(event) => updateLine(line.key, { description: event.target.value })}
                    aria-label={`Line ${index + 1} description`}
                    required
                    style={{ width: '100%' }}
                  />
                </Field>
                <Field
                  compact
                  label={index === 0 ? 'Qty' : ''}
                  htmlFor={`invoice_line_qty_${line.key}`}
                >
                  <input
                    id={`invoice_line_qty_${line.key}`}
                    className="ln-filter-input"
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    value={line.quantity}
                    onChange={(event) => updateLine(line.key, { quantity: event.target.value })}
                    aria-label={`Line ${index + 1} quantity`}
                    required
                    style={{ width: '100%' }}
                  />
                </Field>
                <Field
                  compact
                  label={index === 0 ? 'Unit amount' : ''}
                  htmlFor={`invoice_line_amount_${line.key}`}
                >
                  <input
                    id={`invoice_line_amount_${line.key}`}
                    className="ln-filter-input"
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={line.unitAmount}
                    onChange={(event) => updateLine(line.key, { unitAmount: event.target.value })}
                    aria-label={`Line ${index + 1} unit amount in dollars`}
                    placeholder="0.00"
                    required
                    style={{ width: '100%' }}
                  />
                </Field>
                <button
                  type="button"
                  className="ln-toolbar-btn"
                  onClick={() => removeLine(line.key)}
                  disabled={form.data.lines.length === 1}
                  style={{ marginBottom: 0, opacity: form.data.lines.length === 1 ? 0.5 : 1 }}
                >
                  Remove
                </button>
              </div>
            ))}
            {message(form.errors.lines) ? (
              <div className="ln-error" style={{ marginBottom: 12 }}>
                {message(form.errors.lines)}
              </div>
            ) : null}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 20px' }}>
              <button type="button" className="ln-toolbar-btn" onClick={addLine}>
                Add line
              </button>
              <span className="ln-row-meta" style={{ marginLeft: 'auto' }}>
                <span className="ln-inbox-sub">Total {formatUsdFromCents(totalCents)}</span>
              </span>
            </div>

            {extraError(form.errors) ? (
              <div className="ln-error" style={{ marginBottom: 12 }}>
                {extraError(form.errors)}
              </div>
            ) : null}

            <button
              type="submit"
              className="ln-primary-btn"
              disabled={form.processing}
              style={{ marginLeft: 0 }}
            >
              Create invoice
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  error,
  compact = false,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: unknown;
  compact?: boolean;
  children: ReactNode;
}) {
  const text = message(error);

  return (
    <div style={{ marginBottom: compact ? 0 : 16 }}>
      {label ? (
        <label
          htmlFor={htmlFor}
          style={{
            display: 'block',
            fontSize: '0.75rem',
            color: 'var(--ln-muted)',
            marginBottom: 6,
          }}
        >
          {label}
        </label>
      ) : null}
      {children}
      {text ? <div className="ln-error">{text}</div> : null}
    </div>
  );
}

const shownErrorKeys = new Set([
  'customerId',
  'customerName',
  'customerEmail',
  'memo',
  'dueOn',
  'lines',
]);

function extraError(errors: object): string | null {
  const parts = Object.entries(errors)
    .filter(([key]) => !shownErrorKeys.has(key))
    .map(([, value]) => message(value))
    .filter((value): value is string => Boolean(value));

  return parts.length ? parts.join(' ') : null;
}

function message(error: unknown): string | null {
  if (typeof error === 'string') return error || null;

  if (Array.isArray(error)) {
    const parts = error
      .map((item) => message(item))
      .filter((item): item is string => Boolean(item));
    return parts.length ? parts.join(' ') : null;
  }

  if (error && typeof error === 'object') {
    const parts = Object.values(error)
      .map((item) => message(item))
      .filter((item): item is string => Boolean(item));
    return parts.length ? parts.join(' ') : null;
  }

  return null;
}

function buildInitialForm(invoice: InvoiceDraft | null): InvoiceFormState {
  const lines = invoice?.lines ?? [];
  const customerId = invoice?.customerId || invoice?.customer?.id || '';

  return {
    customerId,
    customerName: customerId ? '' : (invoice?.customer?.name ?? ''),
    customerEmail: customerId ? '' : (invoice?.customer?.email ?? ''),
    memo: invoice?.memo ?? '',
    dueOn: dateInputValue(invoice?.dueOn),
    lines: lines.length
      ? lines.map((line, index) => ({
          key: `line-${index + 1}`,
          description: line.description ?? '',
          quantity: line.quantity == null || line.quantity === '' ? '1' : String(line.quantity),
          unitAmount: centsToDollarInput(line.unitAmountCents),
        }))
      : [blankLine('line-1')],
  };
}

function blankLine(key: string): LineDraft {
  return { key, description: '', quantity: '1', unitAmount: '' };
}

function dateInputValue(value: string | null | undefined): string {
  if (!value) return '';

  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match ? match[1] : '';
}

function centsToDollarInput(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '';

  return (cents / 100).toFixed(2);
}

function wholeQuantity(value: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;

  return Math.round(parsed);
}

function dollarsToCents(input: string): number {
  const trimmed = input.trim().replace(/\$/g, '').replace(/,/g, '');
  if (!trimmed) return 0;

  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const match = /^(\d*)(?:\.(\d*))?$/.exec(unsigned);
  if (!match) return 0;

  const whole = match[1] ? Number(match[1]) : 0;
  const fraction = match[2] ?? '';
  const hundredths = Number((fraction + '00').slice(0, 2));
  const roundUp = fraction[2] !== undefined && Number(fraction[2]) >= 5 ? 1 : 0;
  const cents = whole * 100 + hundredths + roundUp;

  return negative ? -cents : cents;
}
